import type { BonusKind, Color, GameAction, GameState, Move, PieceId, PieceState } from '@ccc/shared-types';

/**
 * A RuleModifier hooks into the engine pipeline.
 *
 * Each card maps to ONE modifier. The base chess rules are also expressed as
 * a modifier so the pipeline is uniform.
 *
 * Hooks (all optional):
 *
 * - extendLegalMoves: add moves to a piece's pseudo-legal set.
 * - filterLegalMoves: remove moves from a piece's pseudo-legal set.
 * - canCapture: short-circuit capture eligibility (null = no opinion).
 * - extendSpecialMoves: special non-piece-attached moves (e.g. global swap).
 * - extendSpecialActions: special non-move actions (e.g. store tempo).
 * - onMoveResolve: mutate game state alongside move execution.
 * - onTurnEnd: evaluate end conditions; return non-null to end the game.
 * - reactToCapture: triggered when ANY piece is captured (used by
 *   Retaliatory Strike, Royal Escort, etc.).
 * - turnBonusGranted: after the main move, may grant a free bonus move
 *   (used by Momentum Check, Stored Tempo, Knight Double Trot, etc.).
 *
 * Modifiers run in priority order (descending).
 */
export interface RuleModifier {
  id: string;
  ownerSide: Color | 'shared';
  conflictTags: string[];
  priority: number;
  hooks: {
    extendLegalMoves?: (piece: PieceState, state: GameState) => Move[];
    filterLegalMoves?: (piece: PieceState, moves: Move[], state: GameState) => Move[];
    canCapture?: (attacker: PieceState, target: PieceState, state: GameState) => boolean | null;
    extendSpecialMoves?: (state: GameState) => Move[];
    extendSpecialActions?: (state: GameState) => GameAction[];
    onMoveResolve?: (move: Move, state: GameState) => StateMutation[];
    onTurnEnd?: (state: GameState) => EndConditionResult | null;
    reactToCapture?: (
      captured: PieceState,
      attacker: PieceState,
      state: GameState,
    ) => CaptureReaction | null;
    turnBonusGranted?: (
      move: Move,
      state: GameState,
    ) => TurnBonus | null;
    /**
     * Override loss condition. Default: king captured.
     * Return 'queen' to swap to queen-captured (e.g. Ascendant Royalty).
     * Return null = no opinion.
     */
    overrideLossCondition?: (state: GameState, color: Color) => 'king' | 'queen' | null;
  };
}

/** Atomic state mutations applied by modifiers. Engine applies these in order. */
export type StateMutation =
  | { kind: 'set_flag'; pieceId: string; key: string; value: unknown }
  | { kind: 'set_rule_state'; cardId: string; value: unknown }
  | { kind: 'remove_piece'; pieceId: string }
  | { kind: 'spawn_piece'; piece: PieceState }
  | { kind: 'transform_piece'; pieceId: string; toType: PieceState['type'] };

export interface EndConditionResult {
  ended: true;
  winner: Color | 'draw';
  reason:
    | 'king_captured'
    | 'queen_captured'
    | 'resignation'
    | 'timeout'
    | 'draw_agreed'
    | 'stalemate_legal_moves_only'
    | 'abandoned';
}

export interface CaptureReaction {
  /** Player who gets a bonus move because of this capture (e.g. Retaliatory Strike). */
  bonusFor?: Color;
  /** Notes for replay annotation. */
  note?: string;
}

export interface TurnBonus {
  /** Color who gets a bonus move. */
  side: Color;
  /** What kind of bonus this is, for UI labeling. */
  kind: BonusKind;
  /** Whether the bonus move is allowed to capture. */
  canCapture: boolean;
  /** Optional piece-restriction: only these pieces may be moved with the bonus. */
  restrictToPieceIds?: PieceId[];
  /** Optional distance cap for bonus movement. */
  maxDistance?: number;
}

/** Compose all active modifiers into a single ordered list. */
export function composeModifiers(modifiers: RuleModifier[]): RuleModifier[] {
  return [...modifiers].sort((a, b) => b.priority - a.priority);
}
